import {
  PRESET_TAGS,
  PRESET_TYPES,
  VITAL_EXPORT_PROFILES,
  VITAL_TARGET_VERSIONS,
  clearGenerationOptions,
  formatGenerationOptions,
  hasSelectedGenerationOptions,
  resolvedVitalTargetVersion,
  setVitalTargetVersion,
  togglePresetTag,
  togglePresetType,
  type PresetGenerationOptions,
  type PresetTagValue,
  type PresetTypeValue,
  type VitalTargetVersion,
} from "../lib/generation-options.js";

export type GenerationOptionsProps = {
  value: PresetGenerationOptions;
  open: boolean;
  disabled?: boolean;
  onChange: (value: PresetGenerationOptions) => void;
  onOpenChange: (open: boolean) => void;
};

export function GenerationOptions({
  value,
  open,
  disabled = false,
  onChange,
  onOpenChange,
}: GenerationOptionsProps) {
  const hasSelection = hasSelectedGenerationOptions(value);
  const summary = hasSelection ? formatGenerationOptions(value) : "None";
  const targetVersion = resolvedVitalTargetVersion(value);

  return (
    <details
      className="generation-options"
      open={open}
      onToggle={(event) => {
        const next = event.currentTarget.open;
        if (next !== open) onOpenChange(next);
      }}
    >
      <summary className="generation-options-summary">
        <span className="generation-options-chevron" aria-hidden="true">
          {open ? "▾" : "▸"}
        </span>
        <span className="generation-options-summary-label">Preset options</span>
        <span className="generation-options-summary-value" aria-live="polite">
          {summary}
        </span>
      </summary>

      <div className="generation-options-body" id="generation-options-panel">
        <div className="generation-options-section">
          <label className="generation-options-heading" htmlFor="vital-target-version">
            Vital version
          </label>
          <select
            id="vital-target-version"
            className="generation-options-select"
            disabled={disabled}
            value={targetVersion}
            onChange={(event) =>
              onChange(setVitalTargetVersion(value, event.target.value as VitalTargetVersion))
            }
          >
            {VITAL_TARGET_VERSIONS.map((version) => {
              const profile = VITAL_EXPORT_PROFILES[version];
              return (
                <option key={version} value={version}>
                  {profile.label} {profile.stability === "early_access" ? "(Early Access)" : "(Stable)"}
                </option>
              );
            })}
          </select>
        </div>
        <div className="generation-options-section">
          <div className="generation-options-heading-row">
            <p className="generation-options-heading" id="generation-options-type-label">
              Preset type
            </p>
            {hasSelection ? (
              <button
                type="button"
                className="btn ghost btn-compact"
                disabled={disabled}
                onClick={() =>
                  onChange({
                    ...clearGenerationOptions(),
                    ...(value.vitalVersion ? { vitalVersion: value.vitalVersion } : {}),
                  })
                }
              >
                Clear
              </button>
            ) : null}
          </div>
          <div
            className="generation-options-chips"
            role="group"
            aria-labelledby="generation-options-type-label"
          >
            <button
              type="button"
              className="btn ghost btn-compact generation-option-chip"
              disabled={disabled}
              aria-pressed={!value.type}
              onClick={() =>
                onChange({
                  tags: value.tags,
                  ...(value.vitalVersion ? { vitalVersion: value.vitalVersion } : {}),
                })
              }
            >
              None
            </button>
            {PRESET_TYPES.map((entry) => (
              <button
                key={entry.value}
                type="button"
                className="btn ghost btn-compact generation-option-chip"
                disabled={disabled}
                aria-pressed={value.type === entry.value}
                onClick={() =>
                  onChange(togglePresetType(value, entry.value as PresetTypeValue))
                }
              >
                {entry.label}
              </button>
            ))}
          </div>
        </div>

        <div className="generation-options-section">
          <p className="generation-options-heading" id="generation-options-tags-label">
            Tags
          </p>
          <div
            className="generation-options-chips"
            role="group"
            aria-labelledby="generation-options-tags-label"
          >
            {PRESET_TAGS.map((entry) => {
              const selected = value.tags.includes(entry.value);
              return (
                <button
                  key={entry.value}
                  type="button"
                  className="btn ghost btn-compact generation-option-chip"
                  disabled={disabled}
                  aria-pressed={selected}
                  onClick={() =>
                    onChange(togglePresetTag(value, entry.value as PresetTagValue))
                  }
                >
                  {entry.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </details>
  );
}
